import React, {useContext, useEffect, useMemo, useState} from 'react';
import '../../styles/SideBar.css';
import '../../styles/root/home.css';
import {useLocation, useNavigate} from "react-router-dom";
import {AuthContext} from "../../config/AuthContext";
import {Menu, Tooltip} from "antd";
import type {MenuProps} from "antd";
import {BsHouse, BsPerson, BsBoxArrowRight} from "react-icons/bs";

type MenuItem = Required<MenuProps>['items'][number];

const SideBar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {user, userId, actualSite, setActualSite, setLoggedIn} = useContext(AuthContext);

    // Keys de nivell superior (portades) → només una oberta a la vegada (acordió)
    // Usem l'índex per garantir unicitat encara que hi hagi noms repetits o buits.
    const portadaKeys = useMemo(
        () => (user.portada || []).map((_, i) => `portada__${i}`),
        [user.portada]
    );

    const [openKeys, setOpenKeys] = useState<string[]>(() => {
        const idx = (user.portada || []).findIndex(p => p.nom === actualSite);
        return idx >= 0 ? [`portada__${idx}`] : [];
    });

    /**
     * Construeix l'arbre de menú (fins a 3 nivells):
     *  Nivell 1: portada (web) → submenú
     *  Nivell 2: menú secundari (item o contenidor) → item o submenú
     *  Nivell 3: subpàgines dins d'un contenidor → items
     */
    // Mapa: key de la ruta (leaf) → cadena d'ancestres [submenuKey?, portadaKey]
    const ancestorsMap = useMemo(() => new Map<string, string[]>(), [user.portada, user.menus]);
    // Conjunt de keys que són contenidors (títols de grup): NO han de navegar,
    // només desplegar-se. Ex: "Administració", "Comandes".
    const containerKeys = useMemo(() => new Set<string>(), [user.portada, user.menus]);

    const mainItems: MenuItem[] = useMemo(() => {
        const portades = user.portada || [];
        const menus = user.menus || [];
        ancestorsMap.clear();
        containerKeys.clear();

        return portades.map((portada, pIdx) => {
            const portadaKey = `portada__${pIdx}`;
            // Les portades amb nom "root" (Automatitzat, Inscripcions...) comparteixen
            // el menú de root, que ja es mostra a la secció "General". Per tant, aquí
            // es rendereixen com a accés directe al seu enllaç, sense submenú duplicat.
            const esPortadaRoot = portada.nom === 'root';
            const menuPantalla = esPortadaRoot ? undefined : menus.find(m => m.pantalla === portada.nom);

            const subItems: MenuItem[] = (menuPantalla?.menu || [])
                .map((item, idx) => {
                    const teChildren = item.children && item.children.length > 0;
                    if (teChildren) {
                        const subKey = `sub__${pIdx}__${idx}`;
                        const children = item.children.map((child, cIdx) => {
                            const childKey = child.key || `child__${pIdx}__${idx}__${cIdx}`;
                            // 3r nivell: ancestres = submenú (nivell 2) + portada (nivell 1)
                            ancestorsMap.set(childKey, [subKey, portadaKey]);
                            return {key: childKey, label: child.label};
                        });
                        // Contenidor buit → no el rendrem (evita submenús morts que
                        // naveguen a rutes inexistents com /administracio).
                        if (children.length === 0) return null;
                        // Registrem la key del contenidor com a no-navegable.
                        containerKeys.add(subKey);
                        return {key: subKey, label: item.label, children};
                    }
                    const itemKey = item.key || `item__${pIdx}__${idx}`;
                    // Dins d'una portada, les keys de ruta reals sempre contenen '/'
                    // (ex: "menudesign/ingredients"). Si no en conté, és un contenidor
                    // sense fills visibles → l'ometem per no crear un botó mort que
                    // navega a rutes inexistents (ex: /administracio).
                    if (!itemKey.includes('/')) {
                        containerKeys.add(itemKey);
                        return null;
                    }
                    // 2n nivell: ancestre = portada (nivell 1)
                    ancestorsMap.set(itemKey, [portadaKey]);
                    return {key: itemKey, label: item.label};
                })
                .filter((it) => it !== null) as MenuItem[];

            // Capçalera de la portada amb la mateixa imatge que a Home (imageclass).
            // Traiem el token "caja" perquè aquest imposa la mida gegant de les
            // targetes del Home (480x200px, marges...). Ens quedem només amb la
            // classe de la imatge (ecoambit, artiesport, casals...).
            const imageOnlyClass = (portada.imageclass || '')
                .split(' ')
                .filter(c => c && c !== 'caja')
                .join(' ');
            const portadaLabel = (
                <div className={`sidebar-portada ${imageOnlyClass}`}>
                    <span className="sidebar-portada-title">{portada.titol || portada.nom}</span>
                </div>
            );

            // Si la portada NO té menú secundari → item clicable directe (navega al seu enllaç)
            // Si en té → submenú desplegable amb els subitems
            if (subItems.length === 0) {
                return {
                    key: portadaKey,
                    label: portadaLabel,
                };
            }

            return {
                key: portadaKey,
                label: portadaLabel,
                children: subItems,
            };
        });
    }, [user.portada, user.menus, ancestorsMap, containerKeys]);

    /**
     * Menú "root": el que a Home apareix a la barra superior.
     * No pertany a cap portada, és un menú a part (pantalla === 'root').
     */
    const rootItems: MenuItem[] = useMemo(() => {
        const menuPantalla = (user.menus || []).find(m => m.pantalla === 'root');
        if (!menuPantalla || !menuPantalla.menu) return [];
        return menuPantalla.menu
            .map((item, idx) => {
                if (item.children && item.children.length > 0) {
                    const children = item.children.map((child, cIdx) => ({
                        key: child.key || `rootchild__${idx}__${cIdx}`,
                        label: child.label,
                    }));
                    if (children.length === 0) return null;
                    return {key: `rootsub__${idx}`, label: item.label, children};
                }
                const itemKey = item.key || `rootitem__${idx}`;
                return {key: itemKey, label: item.label};
            })
            .filter((it) => it !== null) as MenuItem[];
    }, [user.menus]);

    const selectedKey = location.pathname.replace(/^\//, '');

    // Quan canvia la ruta, obre automàticament la cadena d'ancestres de l'item
    // seleccionat (portada + submenú), respectant l'acordió de portades.
    useEffect(() => {
        const ancestors = ancestorsMap.get(selectedKey);
        if (!ancestors || ancestors.length === 0) return;

        setOpenKeys(prev => {
            // La portada de l'item és l'últim ancestre
            const portadaAncestor = ancestors[ancestors.length - 1];
            // Mantenim els submenús ja oberts d'aquesta portada + tanquem altres portades
            const senseAltresPortades = prev.filter(
                k => !portadaKeys.includes(k) || k === portadaAncestor
            );
            // Afegim tots els ancestres que faltin
            const next = new Set([...senseAltresPortades, ...ancestors]);
            return Array.from(next);
        });
    }, [selectedKey, ancestorsMap, portadaKeys]);

    function handleClick(e: {key: string; keyPath: string[]}) {
        const portadaKey = e.keyPath[e.keyPath.length - 1];

        // Els contenidors (títols de grup) i les keys de submenú no naveguen:
        // antd s'encarrega de desplegar-los. Evitem navegar a rutes inexistents.
        if (containerKeys.has(e.key) || e.key.startsWith('sub__')) {
            return;
        }

        // Clic sobre una portada (amb o sense submenú)
        if (e.key.startsWith('portada__')) {
            const idx = parseInt(e.key.replace('portada__', ''), 10);
            const portada = (user.portada || [])[idx];
            if (portada) {
                setActualSite(portada.nom);
                // Portada sense submenú → navega al seu enllaç directament
                if (portada.enllac) navigate("/" + portada.enllac.replace(/^\//, ''));
            }
            return;
        }

        // Clic sobre un subitem d'una portada
        if (portadaKey?.startsWith('portada__')) {
            const idx = parseInt(portadaKey.replace('portada__', ''), 10);
            const portada = (user.portada || [])[idx];
            if (portada) setActualSite(portada.nom);
        }
        navigate("/" + e.key);
    }

    function handleRootClick(e: {key: string}) {
        // Els títols de grup del menú root (rootsub__) no naveguen.
        if (e.key.startsWith('rootsub__')) return;
        setActualSite('root');
        navigate("/" + e.key);
    }

    /**
     * Acordió NOMÉS a nivell de portada:
     * - Si s'obre una portada nova, es tanquen les altres portades
     * - Els submenús de nivell 2/3 s'obren i tanquen lliurement
     */
    function onOpenChange(keys: string[]) {
        const obertaPortadaNova = keys.find(
            k => portadaKeys.includes(k) && !openKeys.includes(k)
        );

        if (obertaPortadaNova) {
            // Tanca les altres portades, manté els submenús interns oberts
            const senseAltresPortades = keys.filter(
                k => !portadaKeys.includes(k) || k === obertaPortadaNova
            );
            setOpenKeys(senseAltresPortades);
        } else {
            setOpenKeys(keys);
        }
    }

    function handleLogout() {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        setLoggedIn(false);
        navigate("/");
    }

    // Logo segons la web (mateixa lògica que la pantalla de Login).
    function getLogo() {
        const href = window.location.href.toLowerCase();
        if (href.includes("artiesport")) return "/artiesport.png";
        if (href.includes("ambitescola")) return "/ambitescola.png";
        if (href.includes("mentsinquietes")) return "/inquiringminds.png";
        return "/logo512.png";
    }

    return (
        <aside className="sidebar">
            {/* Logo / capçalera — varia segons la web, com la pantalla de Login */}
            <div className="sidebar-logo" onClick={() => {
                setActualSite("root");
                navigate("/home");
            }}>
                <img src={getLogo()} alt="logo"/>
            </div>

            {/* Menú principal scrollable (portades + menús secundaris + menú root) */}
            <div className="sidebar-menu-scroll">
                <Menu
                    className="sidebar-menu"
                    mode="inline"
                    items={mainItems}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    selectedKeys={[selectedKey]}
                    onClick={handleClick}
                />

                {/* Menú root (general), a part de les seccions */}
                {rootItems.length > 0 && (
                    <>
                        <div className="sidebar-divider">
                            <span>General</span>
                        </div>
                        <Menu
                            className="sidebar-menu sidebar-menu--root"
                            mode="inline"
                            items={rootItems}
                            selectedKeys={[selectedKey]}
                            onClick={handleRootClick}
                        />
                    </>
                )}
            </div>

            {/* Footer: 3 icones en fila, sense text, sempre visibles */}
            <div className="sidebar-footer">
                <Tooltip title="Inici" placement="top">
                    <button
                        className="sidebar-footer-btn"
                        onClick={() => {
                            setActualSite("root");
                            navigate("/home");
                        }}
                    >
                        <BsHouse/>
                    </button>
                </Tooltip>
                <Tooltip title="Perfil" placement="top">
                    <button
                        className="sidebar-footer-btn"
                        onClick={() => navigate(`/profile/${userId}`)}
                    >
                        <BsPerson/>
                    </button>
                </Tooltip>
                <Tooltip title="Sortir" placement="top">
                    <button
                        className="sidebar-footer-btn sidebar-footer-btn--danger"
                        onClick={handleLogout}
                    >
                        <BsBoxArrowRight/>
                    </button>
                </Tooltip>
            </div>
        </aside>
    );
};

export default SideBar;
